import _, { difference, intersection, isArray, isBoolean, isFunction, isNil, isNumber, isString, keyBy, map, reduce, size, trim, uniq, without } from "lodash";
import { init } from "lodash/fp";
import { nanoid } from "nanoid";
import React, { ChangeEvent, Dispatch, Reducer, useCallback, useMemo, useReducer, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaExclamationTriangle, FaTimes } from "react-icons/fa";
import { Button, Col, Dropdown, DropdownItem, DropdownMenu, DropdownToggle, Form, FormGroup, Input, Label, ListGroup, ListGroupItem, Navbar } from "reactstrap";
import { ExtractActionsFromState } from "../types";
import { DistributiveOmit } from "../util";


type UseFiltersProps<T> = {
    data: T[],
    availableFilters: FilterDescription<T>[]
};

type UseFiltersReturn<T> = {
    filtered: T[],
    form: JSX.Element,
    count: number
};

type FilterType = 'number' | 'string' | 'date' | 'list' | 'boolean';

type FilterDataByType<T, V extends keyof T = keyof T> = {
    number: NumberFilterData<T, V>,
    string: StringFilterData<T, V>,
    date: DateFilterData<T, V>,
    list: ListFilterData<T, V>,
    boolean: BooleanFilterData<T, V>,
}



export type FilterDescription<T, V extends keyof T = keyof T, K extends FilterType = FilterType> = {
    field: V,
    title: string,
    type: K,
    selector?: (p: T) => T[V],
    initialState?: DistributiveOmit<FilterDataByType<T,V>[K], 'field'|'isValid'|'type'>
};

type FilterData<T, V extends keyof T = keyof T, K extends FilterType = FilterType> = Readonly<{ field: V, isValid: boolean, type: K }/* & (({ type: 'number' } & NumberFilterData)
    | ({ type: 'string' } & StringFilterData) | ({ type: 'date' } & DateFilterData)
    | ({ type: 'list' } & ListFilterData) | ({ type: 'boolean' } & BooleanFilterData))*/>

type FiltersAction<T, V extends keyof T = keyof T> = { type: 'add', field: V, filterType: FilterType }
    | { type: 'change', id: string, data: Partial<FilterData<T>> }
    | { type: 'remove', id: string }
    | { type: 'removeAll' }

type FiltersState<T> = Readonly<{
    filters: Record<string, FilterData<T>>
}>

type FiltersReducer<T> = Reducer<FiltersState<T>, FiltersAction<T>>
//type FilterReducerState<T> = ReducerState<FilterReducer<T>>
//type FilterReducerAction = ReducerAction<>;;

//const defaultFilterData: Record<FilterType, Partial<FilterData<any>>> = {
//    'number': {
//        mode: 'between',
//        min: 0,
//        max: 0
//    } as NumberFilterData,
//    'string': {
//        mode: 'contains',
//        text: ''
//    } as StringFilterData,
//    'date': {
//        mode: 'between',
//        from: '',
//        to: ''
//    },
//    'list': {
//        mode: 'all',
//        items: []
//    },
//    'boolean': {
//        value: true
//    }
//};

const add = <T,>(prevState: FiltersState<T>, field: keyof T, type: FilterType) => {
    let data;

    switch (type) {
        case 'number':
            data = { mode: 'between', min: 0, max: 0, isValid: true, }
            break;
        case 'string':
            data = { mode: 'contains', text: '' }
            break;
        case 'date':
            data = { mode: 'between', from: '', to: '' }
            break;
        case 'list':
            data = { mode: 'all', items: [] }
            break;
        case 'boolean':
            data = { isValid: true, value: true }
            break;
        default:
            throw 'Unreachable error';
    }

    return {
        ...prevState, filters: {
            ...prevState.filters,
            // @ts-ignore
            [nanoid()]: { type, field, isValid: false, ...data }
        }
    }
};

const change = <T,>(prevState: FiltersState<T>, id: string, data: Partial<FilterData<T>>) => ({
    ...prevState, filters: {
        ...prevState.filters,
        [id]: { ...prevState.filters[id], ...data }
    }
});

const remove = <T,>(prevState: FiltersState<T>, id: string) => {
    const filters = { ...prevState.filters };

    delete filters[id];

    return { ...prevState, filters }
};

const filterReducer = <T,>(prevState: FiltersState<T>, action: FiltersAction<T>) => {
    switch (action.type) {
        case 'add':
            return add<T>(prevState, action.field, action.filterType);
        case 'change':
            return change<T>(prevState, action.id, action.data);
        case 'remove':
            return remove<T>(prevState, action.id);
        case 'removeAll':
            return { ...prevState, filters: {} };
        default:
            throw new Error();
    }
}

const useFilters = <T,>({ data, availableFilters }: UseFiltersProps<T>): UseFiltersReturn<T> => {

    //const [filters, setFilters] = useState<FilterDescription<T>[]>();

    const [state, dispatch] = useReducer<FiltersReducer<T>, typeof availableFilters>(filterReducer, availableFilters, (availableFilters) => {
        const filters: FiltersState<T>['filters'] = {};

        let l = availableFilters.length;

        for (let i = 0; i < l; i++) {
            const { initialState, field, type } = availableFilters[i];

            if (initialState === undefined) {
                continue;
            }

            filters[nanoid()] = { field, type, isValid: true, ...initialState };
        }

        return { filters };
    });

    const filtersRecord = useMemo(() => keyBy(availableFilters, 'field') as FilterDescriptionsRecord<T>, [availableFilters])

    const { filters: filtersData } = state;

    const filters = useMemo(() => {
        return reduce(filtersData, (filters, filterData) => {

            if (filterData.isValid === false) {
                return filters;
            }

            const description = filtersRecord[filterData.field] as FilterDescription<T, typeof filterData.field, typeof filterData.type>;

            switch (filterData.type) {
                case 'number':
                    filters.push(numberFilter<T, typeof filterData.field>(description, filterData as NumberFilterData<T, typeof filterData.field>));
                    break;
                case 'string':
                    filters.push(stringFilter<T, typeof filterData.field>(description, filterData as StringFilterData<T, typeof filterData.field>));
                    break;
                case 'date':
                    filters.push(dateFilter<T, typeof filterData.field>(description, filterData as DateFilterData<T, typeof filterData.field>));
                    break;
                case 'list':
                    filters.push(listFilter<T, typeof filterData.field>(description, filterData as ListFilterData<T, typeof filterData.field>));
                    break;
                case 'boolean':
                    filters.push(booleanFilter<T, typeof filterData.field>(description, filterData as BooleanFilterData<T, typeof filterData.field>));
                    break;
            }

            return filters;
        }, [] as ((d: T) => boolean)[]);
    }, [filtersData, filtersRecord]);


    const filtered = useMemo(() => {


        const l = filters.length;

        if (filters.length === 0) {
            return data;
        }

        return reduce(data, (filtered, d) => {


            for (let i = 0; i < l; i++) {
                const filter = filters[i];

                if (filter(d) === false) {
                    return filtered;
                }
            }

            filtered.push(d);

            return filtered;
        }, [] as T[]);
    }, [data, filters]);

    //const prompt = useMemo(() => <div>
    //    <Modal isOpen={true}>
    //        <ModalHeader>{title}</ModalHeader>
    //        <ModalBody>{text}</ModalBody>
    //        <ModalFooter>
    //            <Button color="primary" onClick={onConfirm}> Do Something </Button>{' '}
    //            <Button color="secondary" onClick={() => onCancel?.()}> Cancel </Button>
    //        </ModalFooter>
    //    </Modal>
    //</div>, [onConfirm, onCancel]);

    const form = useMemo(() => {
        return <FiltersForm data={data} filters={filtersData} filtersRecord={filtersRecord} dispatch={dispatch} />
    }, [filters, availableFilters, dispatch])

    return { form, filtered, count: size(filters) };
};

export default useFilters;

type FilterDescriptionsRecord<T> = {
    [V in Partial<keyof T>]: FilterDescription<T, V>;
}

type FiltersFormProps<T> = {
    filters: Record<string, FilterData<T>>
    filtersRecord: FilterDescriptionsRecord<T>
    dispatch: Dispatch<FiltersAction<T>>,
    data: T[]
}

type FilterFormProps<T, V extends keyof T = keyof T, F extends FilterData<T, V> = FilterData<T, V>> = {
    id: string,
    filter: F,
    description: FilterDescription<T, V, F['type']>,
    dispatch: Dispatch<FiltersAction<T, V>>,
    data: T[]
}

export const FiltersForm = <T,>({ filtersRecord, filters, dispatch, data }: FiltersFormProps<T>) => {
    const { t } = useTranslation('default');

    const [dropdownOpen, setDropdownOpen] = useState(false);

    const toggle = useCallback(() => setDropdownOpen((prevState) => !prevState), [setDropdownOpen]);

    const removeFilter = useCallback((id: string) => dispatch({ type: 'remove', id }), [dispatch]);

    const removeAllFilters = useCallback(() => dispatch({ type: 'removeAll' }), [dispatch]);

    const removeAllFiltersButton = useMemo(() => <Button
        color="danger"
        title={t('Rimuovi tutti i filtri').toString()}
        onClick={() => removeAllFilters()}
        disabled={size(filters) === 0}>

        {t('Pulisci')}
    </Button>, [filters, removeAllFilters, t]);

    const filtersDropdown = useMemo(() => <Dropdown isOpen={dropdownOpen} toggle={toggle}>
        <DropdownToggle caret>{t('Aggiungi')}</DropdownToggle><DropdownMenu>
            {map(filtersRecord, ({ title, field, type }) => <DropdownItem key={field as string} onClick={() => dispatch({ type: 'add', field, filterType: type })}>{title}</DropdownItem>)}
        </DropdownMenu>
    </Dropdown>, [filtersRecord, dropdownOpen, toggle]);

    const navbar = useMemo(() => <Navbar style={{ position: 'sticky', top: 0, zIndex: 200, backgroundColor: '#fff' }}>
        <strong>{t(`Filtri`)}</strong>
        <div className='navbar-right ml-2' style={{ display: 'flex' }}>
            {filtersDropdown}
            &nbsp;
            {removeAllFiltersButton}
        </div>
    </Navbar>, [t, dropdownOpen, toggle, filtersDropdown, removeAllFiltersButton]);

    const filtersList = useMemo(() => map(filters, (filter, id) => {
        const { field } = filter;
        const description = filtersRecord[field];
        const { title } = description;

        const form = getFilter<T>({ id, description, filter, data, dispatch });

        return <ListGroupItem key={id} className='p-0'>
            <Navbar>
                <span>
                    {title}
                    &nbsp;
                    {filter.isValid ? null : <FaExclamationTriangle title={t('Filtro non attivo').toString()} className="text-warning" />}
                </span>
                <div className='navbar-right'>
                    <Button color="link" onClick={() => removeFilter(id)}><FaTimes /></Button>
                </div>
            </Navbar>
            <div className='pl-3 pr-3 pb-2 pt-1'>{form}</div>
        </ListGroupItem>

    }), [filters, filtersRecord]);

    return <div style={{ width: '25vw', height: '100%', overflowY: 'auto', backgroundColor: '#eee' }}>
        {navbar}

        <ListGroup className="m-1">{filtersList}</ListGroup>
    </div>
}

const getFilter = <T, V extends keyof T = keyof T>(props: FilterFormProps<T, V>) => {
    const { id, description, filter, data, dispatch } = props;
    const { type } = description;

    switch (description.type) {
        case 'number':
            return <NumberFilterForm<T, V> key={id} filter={filter as NumberFilterData<T, V>} description={description as FilterDescription<T, V, 'number'>} {...{ id, data, dispatch }} />;
        case 'string':
            return <StringFilterForm<T, V> key={id} filter={filter as StringFilterData<T, V>} description={description as FilterDescription<T, V, 'string'>} {...{ id, data, dispatch }} />;
        case 'date':
            return <DateFilterForm<T, V> key={id} filter={filter as DateFilterData<T, V>} description={description as FilterDescription<T, V, 'date'>} {...{ id, data, dispatch }} />;
        case 'list':
            return <ListFilterForm<T, V> key={id} filter={filter as ListFilterData<T, V>} description={description as FilterDescription<T, V, 'list'>} {...{ id, data, dispatch }} />;
        case 'boolean':
            return <BooleanFilterForm<T, V> key={id} filter={filter as BooleanFilterData<T, V>} description={description as FilterDescription<T, V, 'boolean'>} {...{ id, data, dispatch }} />;
    }
}

/* NUMBER */
type NumberFilterData<T, V extends keyof T = keyof T> = FilterData<T, V, 'number'> & {
    mode: 'between',
    min: number,
    max: number
}

const numberFilter = <T, V extends keyof T = keyof T>({ selector, field }: FilterDescription<T, V>, filterData: NumberFilterData<T, V>) => {
    const { mode } = filterData;

    switch (mode) {
        case 'between':
            const min = filterData.min;
            const max = filterData.max;

            return (data: T) => {
                const val: T[V] = selector === undefined ? data[field] : selector(data);

                if (!isNumber(val)) {
                    return false;
                }

                return val >= min && val <= max;
            }
        default:
            throw 'Unreachable error';
    }
}

const validateNumberFilterData = <T, V extends keyof T = keyof T>({ min, max }: Omit<NumberFilterData<T, V>, keyof FilterData<T, V, 'date'>>) => {
    return !isNaN(min) && !isNaN(max) && min <= max;
}

export type NumberFilterFormProps<T, V extends keyof T = keyof T> = FilterFormProps<T, V, NumberFilterData<T, V>>

export type NumberFilterMode = 'between'

export const NumberFilterForm = <T, V extends keyof T = keyof T>({ id, data, description, dispatch, filter }: NumberFilterFormProps<T, V>) => {
    if (filter.type !== 'number') {
        throw "Unreachable Error";
    }

    const [mode, setMode] = useState<NumberFilterMode>('between');
    const { field } = description;

    const [min, setMin] = useState<number>(filter.min ?? 0);
    const [max, setMax] = useState<number>(filter.max ?? 0);

    //const isValid = useMemo(() => validateDateFilterData({from, to, mode}), [from, to, mode])

    const { t } = useTranslation('default');

    const setFilter = useCallback((filterParams: { min?: number, max?: number }) => {

        const data = {
            min: filterParams.min ?? min,
            max: filterParams.max ?? max,
            mode
        }

        const isValid = validateNumberFilterData(data);

        dispatch({
            type: 'change', id, data: {
                type: 'number',
                isValid,
                field,
                ...data
            }
        });
    }, [id, field, min, max, dispatch, mode]);

    const handleMinChange = useCallback((event : ChangeEvent<HTMLInputElement>) => {
        const min = Number(event.target.value);
        setMin(min);
        setFilter({ min })
    }, [setMin, setFilter]);

    const handleMaxChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        const max = Number(event.target.value);
        setMax(max);
        setFilter({ max })

    }, [setMax, setFilter]);

    return <Form tag="div">
        <FormGroup row>
            <Label sm={2} for={`numberFilterTo-${id}`} className='form-label'>{t('Da')}</Label>
            <Col sm={10}>
                <Input type='number' defaultValue={min} onChange={handleMinChange} max={max} />
            </Col>
        </FormGroup>

        <FormGroup row>
            <Label sm={2} for={`numberFilterFrom-${id}`} className='form-label'>{t('A')}</Label>
            <Col sm={10}>
                <Input type='number' defaultValue={max} onChange={handleMaxChange} min={min} />
            </Col>
        </FormGroup>
    </Form>;
}

// #region StringFilter
type StrigFilterMode = 'contains'

type StringFilterData<T, V extends keyof T = keyof T> = FilterData<T, V, 'string'> & {
    mode: StrigFilterMode,
    text: string
}

const stringFilter = <T, V extends keyof T = keyof T>({ selector, field }: FilterDescription<T, V>, filterData: StringFilterData<T, V>) => {
    const { mode } = filterData;

    switch (mode) {
        case 'contains':
            const text = trim(filterData.text).toUpperCase();

            return (data: T) => {
                const val: T[V] = selector === undefined ? data[field] : selector(data);

                if (!isString(val)) {
                    return false;
                }

                return val.toUpperCase().search(text) > -1;
            }
        default:
            throw 'Unreachable error';
    }
}

const validateStringFilterData = <T, V extends keyof T = keyof T>({ text }: Omit<StringFilterData<T, V>, keyof FilterData<T, V, 'string'>>) => {
    return trim(text).length > 0;
}

export type StringFilterFormProps<T, V extends keyof T = keyof T> = FilterFormProps<T, V, StringFilterData<T, V>>

export const StringFilterForm = <T, V extends keyof T = keyof T>({ id, data, description, dispatch, filter }: StringFilterFormProps<T, V>) => {
    const [mode, setMode] = useState<StrigFilterMode>('contains');
    const { field } = description;

    const [text, setText] = useState<string>(filter.text ?? '');

    //const isValid = useMemo(() => validateDateFilterData({from, to, mode}), [from, to, mode])

    const { t } = useTranslation('default');

    const setFilter = useCallback((filterParams: { text: string }) => {

        const data = {
            text: trim(filterParams.text ?? text),
            mode
        }

        const isValid = validateStringFilterData<T, V>(data);

        dispatch({
            type: 'change', id, data: {
                type: 'string',
                isValid,
                ...data
            }
        });
    }, [id, field, text, dispatch, mode]);

    const handleTextChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        const text = event.target.value;
        setText(text);
        setFilter({ text })

    }, [setText, setFilter]);

    return <Form tag="div">
        <FormGroup row>
            <Label sm={4} for={`stringFilterText-${id}`} className='form-label'>{t('Contiene')}</Label>
            <Col sm={8}>
                <Input type='text' defaultValue={text} onChange={handleTextChange} />
            </Col>
        </FormGroup>
    </Form>;
}
// #endregion

/* DATE */
type DateFilterAction = ExtractActionsFromState<DateFilterFormData>

type DateFilterState<T, V extends keyof T = keyof T> = Readonly<DateFilterFormData & {
    id: string,
    field: V,
    isValid: boolean,
    dispatchFilter: React.Dispatch<FiltersAction<T, V>>
}>

type DateFilterMode = 'between' | 'from' | 'to' | 'year' | 'month' | 'week' | 'day'

type DateFilterReducer<T, V extends keyof T = keyof T> = Reducer<DateFilterState<T, V>, DateFilterAction>

type DateFilterFormData = {
    mode: DateFilterMode,
    from: string,
    to: string,
    year: string,
    month: string,
    week: string,
    date: string
}

type DateFilterData<T, V extends keyof T = keyof T> = FilterData<T, V, 'date'> & DateFilterFormData

const dateFilter = <T, V extends keyof T = keyof T>({ field, selector }: FilterDescription<T, V>, filterData: DateFilterData<T, V>) => {
    const { mode } = filterData;

    switch (mode) {
        case 'between':
            const from = Date.parse(filterData.from);
            const to = Date.parse(filterData.to);

            return (data: T) => {
                const val: T[V] = selector === undefined ? data[field] : selector(data);

                if (!isString(val)) {
                    return false;
                }

                const dt = Date.parse(val);

                return dt >= from && dt < to;
            }
        case 'year':
            const year = new Date(Date.parse(filterData.year)).getFullYear();

            return (data: T) => {
                const val: T[V] = selector === undefined ? data[field] : selector(data);

                if (!isString(val)) {
                    return false;
                }
                const y = new Date(Date.parse(val)).getFullYear();

                return y === year;
            }
        default:
            throw 'Unreachable error';
    }
}

const validateDateFilterData = ({ from, to }: DateFilterFormData) => {
    const dateFrom = Date.parse(from);
    const dateTo = Date.parse(to);
    return !isNaN(dateFrom) && !isNaN(dateTo) && dateFrom <= dateTo;
}

export type DateFilterFormProps<T, V extends keyof T = keyof T> = FilterFormProps<T, V, DateFilterData<T, V>>

const dateFilterReducer = <T, V extends keyof T = keyof T>(prevState: DateFilterState<T, V>, action: DateFilterAction) => {
    const { dispatchFilter, id, field, ...data } = prevState;
    (data as Record<typeof action.type, string>)[action.type] = action.value;

    const isValid = validateDateFilterData(data);

    dispatchFilter({
        type: 'change', id, data: {
            type: 'date',
            ...data,
            isValid,
            field
        }
    });

    return {
        ...prevState,
        ...data,
        isValid
    };
};

export const DateFilterForm = <T, V extends keyof T = keyof T>({ id, data, description, dispatch: dispatchFilter, filter }: DateFilterFormProps<T, V>) => {
    if (filter.type !== 'date') {
        throw "Unreachable Error";
    }

    const { field } = description;
    const [state, dispatch] = useReducer<DateFilterReducer<T, V>>(dateFilterReducer, { ...filter, dispatchFilter, id, field });

    const { from, to } = state;

    const [mode, setMode] = useState<DateFilterMode>(filter.mode ?? 'between');

    //const [from, setFrom] = useState<string>(filter.from ?? '');
    //const [to, setTo] = useState<string>(filter.to ?? '');

    const [year, setYear] = useState<string>(filter.year ?? '');
    const [month, setMonth] = useState<string>(filter.month ?? '');
    const [week, setWeek] = useState<string>(filter.week ?? '');
    const [date, setDate] = useState<string>(filter.date ?? '');

    //const isValid = useMemo(() => validateDateFilterData({ from, to, mode }), [from, to, mode])

    const { t } = useTranslation('default');

    const setFilter = useCallback((filterParams: { from?: string, to?: string, mode?: DateFilterMode, year?: string, month?: string, week?: string, date?: string }) => {

        const data = {
            from: filterParams.from ?? from,
            to: filterParams.to ?? to,
            year: filterParams.year ?? year,
            month: filterParams.month ?? month,
            week: filterParams.week ?? week,
            date: filterParams.date ?? date,
            mode: filterParams.mode ?? mode,
        }

        const isValid = validateDateFilterData(data);

        dispatchFilter({
            type: 'change', id, data: {
                type: 'date',
                isValid,
                field,
                ...data
            }
        });
    }, [id, field, from, to, dispatchFilter, mode]);

    const handleChange = useCallback((event:ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;

        dispatch({ type: name, value } as DateFilterAction);
    }, [dispatch])

    //const handleModeChange = useCallback((event) => {
    //    const mode = event.target.value;
    //    setMode(mode);
    //    setFilter({ mode });
    //}, [setMode, setFilter]);

    //const handleFromChange = useCallback((event) => {
    //    const from = event.target.value;
    //    setFrom(from);
    //    setFilter({ from });
    //}, [setFrom, setFilter]);

    //const handleToChange = useCallback((event) => {
    //    const to = event.target.value;
    //    setTo(to);
    //    setFilter({ to });
    //}, [setTo, setFilter]);

    const handleYearChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        const year = event.target.value;
        setYear(year);
        setFilter({ year });
    }, [setYear, setFilter]);

    const handleMonthChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        const month = event.target.value;
        setMonth(month);
        setFilter({ month });
    }, [setMonth, setFilter]);

    const handleWeekChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        const week = event.target.value;
        setWeek(week);
        setFilter({ week });
    }, [setWeek, setFilter]);

    const handleDayChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        const date = event.target.value;
        setWeek(date);
        setFilter({ date });
    }, [setDate, setFilter]);

    const betweenForm = useMemo(() => <>
        <FormGroup row>
            <Label sm={2} for={`dateFilterTo-${id}`} className='form-label'>{t('Da')}</Label>
            <Col sm={10}>
                <Input type='datetime-local' name='from' defaultValue={from} onChange={handleChange} max={to} />
            </Col>
        </FormGroup>

        <FormGroup row>
            <Label sm={2} for={`dateFilterFrom-${id}`} className='form-label'>{t('A')}</Label>
            <Col sm={10}>
                <Input type='datetime-local' name='to' defaultValue={to} onChange={handleChange} min={from} />
            </Col>
        </FormGroup>
    </>, [id, from, to, handleChange, t]);

    const yearForm = useMemo(() =>
        <FormGroup row>
            <Label sm={2} for={`dateFilterYear-${id}`} className='form-label'>{t('Anno')}</Label>
            <Col sm={10}>
                <Input type='number' value={year} defaultValue={new Date().getFullYear()} onChange={handleYearChange} min={1980} max={new Date(Date.now()).getFullYear()} />
            </Col>
        </FormGroup>, [id, year, handleYearChange, t]);

    const monthForm = useMemo(() =>
        <FormGroup row>
            <Label sm={2} for={`dateFilterMonth-${id}`} className='form-label'>{t('Mese')}</Label>
            <Col sm={10}>
                <Input type='month' value={month} defaultValue={new Date().getFullYear()} onChange={handleMonthChange} min={new Date(Date.now()).getFullYear()} max={new Date(Date.now()).getFullYear()} />
            </Col>
        </FormGroup>, [id, month, handleMonthChange, t]);

    const weekForm = useMemo(() =>
        <FormGroup row>
            <Label sm={2} for={`dateFilterMonth-${id}`} className='form-label'>{t('Settimana')}</Label>
            <Col sm={10}>
                <Input type='week' value={week} defaultValue={new Date().toISOString()} onChange={handleWeekChange} min={new Date(Date.now()).getFullYear()} max={new Date(Date.now()).toISOString()} />
            </Col>
        </FormGroup>, [id, week, handleWeekChange, t]);

    const dayForm = useMemo(() =>
        <FormGroup row>
            <Label sm={2} for={`dateFilterDay-${id}`} className='form-label'>{t('Giorno')}</Label>
            <Col sm={10}>
                <Input type='date' value={date} defaultValue={new Date().toISOString()} onChange={handleDayChange} min={new Date(Date.now()).getFullYear()} max={new Date(Date.now()).toISOString()} />
            </Col>
        </FormGroup>, [id, date, handleDayChange, t]);

    return <Form tag="div">
        {betweenForm}
    </Form>;
}

/* LIST */
type ListFilterMode = 'all';

type ListFilterData<T, V extends keyof T> = FilterData<T, V, 'list'> & {
    mode: 'all',
    items: string[]
}

//type FilterFactory<T, V extends keyof T = keyof T, K extends FilterType = FilterType, F extends FilterData<T, V, K> = FilterData<T, V, K>> = (description: FilterDescription<T, V, K>, filterData: F) => (data: T) => boolean

const listFilter = <T, V extends keyof T = keyof T>({ field, selector }: FilterDescription<T, V>, filterData: ListFilterData<T, V>) => {
    const { mode } = filterData;

    switch (mode) {
        case 'all':
            const items = filterData.items;
            const l = items.length;

            return (data: T) => {
                const val: T[V] = selector === undefined ? data[field] : selector(data);

                return isArray(val)
                    ? (difference(items, _(val).filter(isString).uniq().value()).length !== l)
                    : !isNil(val) && isString(val) && items.indexOf(val.toString()) >= 0
            }
        default:
            throw 'Unreachable error';
    }
}

const validateListFilterData = <T, V extends keyof T = keyof T>({ items }: Omit<ListFilterData<T, V>, keyof FilterData<T, V, 'list'>>) => {
    return items.length > 0;
}

export type ListFilterFormProps<T, V extends keyof T = keyof T> = FilterFormProps<T, V, ListFilterData<T, V>>

export const ListFilterForm = <T, V extends keyof T = keyof T>({ id, data, description, dispatch, filter }: ListFilterFormProps<T, V>) => {
    if (filter.type !== 'list') {
        throw "Unreachable Error";
    }

    const [mode, setMode] = useState<ListFilterMode>(filter.mode);

    const { field, selector } = description;

    const options = useMemo(() => {
        const mapped = (isFunction(selector) ? map(data, selector) : map(data, field)) as any[];
        return _(mapped).flatten().uniq().filter((str) => isString(str) && trim(str).length > 0).value().sort() as string[];
    }, [selector, field, data]);

    const { t } = useTranslation('default');

    const [items, setItems] = useState<string[]>(filter.items ?? []);

    const cleanedItems = useMemo(() => intersection(items, options), [items, options]);

    const setFilter = useCallback((filterParams: { items: string[] }) => {
        const data = {
            mode,
            ...filterParams
        }

        const isValid = validateListFilterData(data);

        dispatch({
            type: 'change', id, data: {
                type: 'list',
                isValid,
                ...data
            }
        });
    }, [id, field, dispatch, mode]);

    //useEffect(() => {
    //    setItems((items) => {
    //        const newItems = difference(items, options);
    //        setFilter({ items: newItems });
    //        return newItems;
    //    });
    //}, [options, setItems]);

    const handleChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        const option = event.target.value;

        const newItems = event.target.checked ? uniq([...cleanedItems, option]) : without(cleanedItems, option);

        setItems(newItems);
        setFilter({ items: newItems });
    }, [cleanedItems, setItems]);

    return <Form tag="div" style={{ display: 'flex', flexWrap: 'wrap', gap: '1em' }}>
        {
            options.map((option) =>
                <FormGroup check key={option} >
                    <Label check>
                        <Input type="checkbox" value={option} defaultChecked={cleanedItems.indexOf(option) >= 0} onChange={handleChange} />
                        {option}
                    </Label>
                </FormGroup>
            )
        }
    </Form>;
}


/* BOOLEAN */
type BooleanFilterData<T, V extends keyof T = keyof T> = FilterData<T, V, 'boolean'> & {
    value: boolean
}

const booleanFilter = <T, V extends keyof T = keyof T>({ field, selector }: FilterDescription<T, V>, filterData: BooleanFilterData<T, V>) => {
    return (data: T) => {
        const val: T[V] = selector === undefined ? data[field] : selector(data);

        if (!isBoolean(val)) {
            return false;
        }

        return val === filterData.value;
    }
}

export type BooleanFilterFormProps<T, V extends keyof T = keyof T> = FilterFormProps<T, V, BooleanFilterData<T, V>>

export const BooleanFilterForm = <T, V extends keyof T = keyof T>({ id, data, description, dispatch, filter }: BooleanFilterFormProps<T, V>) => {
    //const [mode, setMode] = useState<DateFilterMode>('between');
    const { field } = description;

    const [value, setValue] = useState<boolean>(filter.value ?? true);

    //const isValid = useMemo(() => validateDateFilterData({from, to, mode}), [from, to, mode])

    const { t } = useTranslation('default');

    const setFilter = useCallback((filterParams: { value: boolean }) => {

        dispatch({
            type: 'change', id, data: {
                type: 'boolean',
                field,
                ...filterParams
            }
        });
    }, [id, field, value, dispatch]);


    const handleChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value === 'true'
            ? event.target.checked
            : !event.target.checked;

        setValue(value);
        setFilter({ value });
    }, [setValue, setFilter]);

    return <Form tag="div">
        <FormGroup check inline>
            <Input type="radio" value="true" checked={value} onChange={handleChange} />
            <Label check>{t('Sì')}</Label>
        </FormGroup>
        <FormGroup check inline>
            <Input type="radio" value="false" checked={!value} onChange={handleChange} />
            <Label check>{t('No')}</Label>
        </FormGroup>
    </Form>;
}
